/**
 * Ai thấy danh sách đơn nạp thẻ "Đơn hàng › Thẻ cào" (`/quan-ly-don-hang/nap-the`) — MỘT luật dùng
 * chung cho cả ba nơi gác (`sideBar.js` gác nhóm, `navItem.js` gác mục con, `header.js` gác route).
 * Ba nơi tự viết ba luật là cách một nơi lệch đi mà không test nào đỏ (xem CLAUDE.md, "Quyền gác ở
 * BA nơi").
 *
 * 11/09/2026: danh sách mới (FR-W-06) THAY trang cũ ở đúng đường dẫn đó (Quyết định #5) và mục menu
 * "Nạp thẻ" thành mục đơn trỏ thẳng vào wizard.
 *
 * 🔴 Gác bằng `CREATE_TOPUP_ORDER` — ĐÚNG quyền backend gác `GET /topup-batches`
 * (`simplus-backend` erp-gateway `topup-batch.controller.ts:61`, đọc 11/09/2026). Đừng mở thêm
 * `TOPUP_ORDERS_MANAGEMENT` (quyền của trang cũ): hai role chỉ có quyền đó (`external_account` gốc,
 * khách lẻ) sẽ thấy mục menu rồi mở ra ăn 403. Mọi role từng dùng trang cũ đều đã có
 * `CREATE_TOPUP_ORDER`, nên không ai mất lối vào thật. Phạm vi "xem đơn của mọi tài khoản" vẫn là
 * việc của backend (`GET_TOPUP_ORDER_INFO_LIST`), không phải của menu.
 */
import { PERMISSION } from '../../constants/permission'
import { isHasPermission } from '../../redux/reducers/permission'

/** Phần tử của mảng `handlePermission()` trả về — chỉ phần luật này đọc tới. */
export interface UserPermissionEntry {
  role: string
}

/**
 * `null`/`undefined` ⇒ `isHasPermission` tự đọc role của người đang đăng nhập từ `localStorage`
 * (`handlePermission()`), đúng cách các nơi gọi JS đang dùng.
 */
export type UserPermissionList = ReadonlyArray<UserPermissionEntry> | null | undefined

/** `permission.js`/reducer là JS không có kiểu — khai hẹp đúng phần đang dùng rồi ép qua `unknown`. */
const permissionValues = PERMISSION as unknown as Record<string, string>
const hasPermission = isHasPermission as unknown as (
  required: string | string[],
  userPermission: UserPermissionList,
) => boolean

/** Thấy "Đơn hàng › Thẻ cào" — cùng quyền backend gác `GET /topup-batches` (xem đầu file). */
export const canViewTopupOrderList = (userPermission?: UserPermissionList): boolean => (
  hasPermission(permissionValues.CREATE_TOPUP_ORDER, userPermission)
)

/**
 * Nhóm menu "Đơn hàng". Trước đây chỉ `VIEW_LIST_ORDER`; nay thêm người thấy được danh sách đơn nạp
 * thẻ — 5 role nội bộ tạo được đơn nạp thẻ mà không có `VIEW_LIST_ORDER` (hành chính, bổ sung hồ sơ,
 * marketing) sẽ mất lối vào đơn của chính mình nếu nhóm vẫn ẩn. Các mục con KHÁC của nhóm vẫn đòi
 * `VIEW_LIST_ORDER` (`navItem.js`), nên với họ nhóm chỉ có đúng một mục "Thẻ cào".
 */
export const canSeeOrderMenuGroup = (userPermission?: UserPermissionList): boolean => (
  hasPermission(permissionValues.VIEW_LIST_ORDER, userPermission) || canViewTopupOrderList(userPermission)
)

/** Nút "Xuất báo cáo" của danh sách — cùng quyền `EXPORT_CARD` mà trang cũ dùng. */
export const canExportTopupOrderReport = (userPermission?: UserPermissionList): boolean => (
  hasPermission(permissionValues.EXPORT_CARD, userPermission)
)

/**
 * Cột "Thao tác" (nút "Hoàn điểm") của drawer chi tiết lô — ĐÚNG quyền backend gác
 * `POST /topup-batches/items/:id/refund` (`REFUND_ORDER`, `topup-batch.controller.ts:124`).
 *
 * 🔴 Đừng gác bằng `record.refundable` MỘT MÌNH: cờ đó BE tính theo trạng thái record
 * (`isTopupRecordRefundable` chỉ xét `status` + `refunded_at`), KHÔNG xét người xem. Trước 11/09/2026
 * drawer chỉ đọc cờ đó ⇒ CTV/Đại lý/KHDN (và kế toán, người xem được lô của mọi người) thấy nút trên
 * mọi record lỗi, bấm vào ăn 403.
 */
export const canRefundTopupRecord = (userPermission?: UserPermissionList): boolean => (
  hasPermission(permissionValues.REFUND_ORDER, userPermission)
)
